import React, { useState } from "react";
import Seo from "../components/site/Seo";
import { Container, CtaBand, Eyebrow } from "../components/site/Ui";
import { EXAMPLES, EXAMPLE_FILTERS } from "../data/site";
import { useLang } from "../lib/LangContext";


export default function Examples() {
  const { tr } = useLang();
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? EXAMPLES : EXAMPLES.filter((e) => e.industry === filter);

  return (
    <>
      <Seo
        title="Examples — social media content concepts for Dutch businesses"
        description="Browse example social media content concepts by industry: restaurants, cafés, beauty salons, barbers, gyms, automotive, retail and hospitality. Post designs, captions and Reel concepts."
        path="/examples"
      />

      <section className="mesh-warm" data-testid="examples-hero">
        <Container className="py-16 lg:py-20">
          <Eyebrow>{tr("Examples")}</Eyebrow>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {tr("Content concepts, by industry.")}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Everything below is an illustration of what PostMint creates. These are concepts, not customer campaigns —
            each one is clearly labelled as an <strong>Example Concept</strong>.
          </p>
        </Container>
      </section>

      <section className="py-12 sm:py-16">
        <Container>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter examples by industry">
            {EXAMPLE_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`example-filter-${f.toLowerCase().replace(/[^a-z]/g, "") || "all"}`}
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                  filter === f
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                }`}
              >
                {tr(f)}
              </button>
            ))}
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2" data-testid="examples-grid">
            {visible.map((e) => (
              <article
                key={e.id}
                data-testid={`example-card-${e.id}`}
                className="overflow-hidden rounded-3xl border border-slate-200 bg-white soft-shadow"
              >
                <div className="relative">
                  <img
                    src={e.image}
                    alt={`${e.industry} social media content example`}
                    loading="lazy"
                    className="h-56 w-full object-cover"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-900">
                    {tr("Example Concept")}
                  </span>
                  <span className="absolute right-4 top-4 rounded-full bg-[#F95738] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                    {tr(e.industry)}
                  </span>
                </div>

                <div className="space-y-5 p-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{tr("Business type")}</p>
                    <p className="text-base font-semibold text-slate-900">{e.business}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{tr("Objective")}</p>
                    <p className="text-sm text-slate-700">{e.objective}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{tr("Caption")}</p>
                    <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-600">{e.caption}</p>
                    <p className="mt-2 text-xs text-slate-400">{e.hashtags}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{tr("Reel concept")}</p>
                    <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-600">{e.reel}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{tr("Promotional idea")}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{e.promo}</p>
                  </div>
                  <p className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    CTA: {e.cta}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {visible.length === 0 && (
            <p data-testid="examples-empty" className="mt-12 text-center text-slate-500">
              {tr("No examples in this category yet.")}
            </p>
          )}
        </Container>
      </section>

      <CtaBand
        title="Want this for your business?"
        body="Choose a package and we'll build your first content month around your brand."
        testId="examples-final-cta-button"
      />
    </>
  );
}
